import React, { useEffect, useState } from 'react';
import { Card, Table, Tag, Button, Modal, Spin, Empty, Typography, Space, message } from 'antd';
import {
  QuestionCircleOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  SyncOutlined,
  EyeOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import inquiryService from '../../services/inquiryService';
import InquiryModal from '../../components/inquiry/InquiryModal';
import './MyInquiriesPage.scss';

const { Title, Text, Paragraph } = Typography;

const MyInquiriesPage = () => {
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedInquiry, setSelectedInquiry] = useState(null);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [newInquiryModalVisible, setNewInquiryModalVisible] = useState(false);

  useEffect(() => {
    fetchInquiries();
  }, []);

  const fetchInquiries = async () => {
    try {
      setLoading(true);
      const data = await inquiryService.getMyInquiries();
      setInquiries(data);
    } catch (error) {
      message.error('Failed to load inquiries');
    } finally {
      setLoading(false);
    }
  };

  const getStatusTag = (status) => {
    const config = {
      PENDING: { color: 'orange', icon: <ClockCircleOutlined />, text: 'Pending' },
      IN_PROGRESS: { color: 'blue', icon: <SyncOutlined spin />, text: 'In Progress' },
      RESOLVED: { color: 'green', icon: <CheckCircleOutlined />, text: 'Resolved' },
      CLOSED: { color: 'default', icon: <CheckCircleOutlined />, text: 'Closed' },
    };
    const statusConfig = config[status] || config.PENDING;
    return (
      <Tag color={statusConfig.color} icon={statusConfig.icon}>
        {statusConfig.text}
      </Tag>
    );
  };

  const getTypeTag = (type) => {
    const config = {
      PRODUCT: { color: 'blue', text: 'Product' },
      SERVICE: { color: 'green', text: 'Service' },
      GENERAL: { color: 'purple', text: 'General' },
    };
    const typeConfig = config[type] || config.GENERAL;
    return <Tag color={typeConfig.color}>{typeConfig.text}</Tag>;
  };

  const columns = [
    {
      title: 'Date',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 120,
      render: (date) => new Date(date).toLocaleDateString('el-GR'),
    },
    {
      title: 'Type',
      dataIndex: 'inquiry_type',
      key: 'inquiry_type',
      width: 100,
      render: (type) => getTypeTag(type),
    },
    {
      title: 'Subject',
      dataIndex: 'subject',
      key: 'subject',
      ellipsis: true,
    },
    {
      title: 'Related To',
      key: 'related',
      width: 200,
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          {record.product_name && <Text type="secondary">Product: {record.product_name}</Text>}
          {record.service_name && <Text type="secondary">Service: {record.service_name}</Text>}
          {record.partner_name && <Text type="secondary">Partner: {record.partner_name}</Text>}
          {!record.product_name && !record.service_name && !record.partner_name && (
            <Text type="secondary">-</Text>
          )}
        </Space>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status) => getStatusTag(status),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      render: (_, record) => (
        <Button
          type="link"
          icon={<EyeOutlined />}
          onClick={() => {
            setSelectedInquiry(record);
            setDetailModalVisible(true);
          }}
        >
          View
        </Button>
      ),
    },
  ];

  const handleNewInquiryClose = () => {
    setNewInquiryModalVisible(false);
    fetchInquiries();
  };

  if (loading) {
    return (
      <div className="my-inquiries-page loading">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="my-inquiries-page">
      <div className="page-header">
        <Title level={2}>
          <QuestionCircleOutlined /> My Inquiries
        </Title>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => setNewInquiryModalVisible(true)}
        >
          New Inquiry
        </Button>
      </div>

      <Card>
        {inquiries.length === 0 ? (
          <Empty
            description="You haven't submitted any inquiries yet"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          >
            <Button
              type="primary"
              onClick={() => setNewInquiryModalVisible(true)}
            >
              Submit Your First Inquiry
            </Button>
          </Empty>
        ) : (
          <Table
            dataSource={inquiries}
            columns={columns}
            rowKey="id"
            pagination={{ pageSize: 10 }}
          />
        )}
      </Card>

      {/* Inquiry Detail Modal */}
      <Modal
        title={
          <Space>
            <QuestionCircleOutlined />
            <span>Inquiry Details</span>
          </Space>
        }
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setDetailModalVisible(false)}>
            Close
          </Button>,
        ]}
        width={700}
      >
        {selectedInquiry && (
          <div className="inquiry-detail">
            <div className="detail-row">
              <Text strong>Status:</Text>
              {getStatusTag(selectedInquiry.status)}
            </div>

            <div className="detail-row">
              <Text strong>Type:</Text>
              {getTypeTag(selectedInquiry.inquiry_type)}
            </div>

            <div className="detail-row">
              <Text strong>Subject:</Text>
              <Text>{selectedInquiry.subject}</Text>
            </div>

            {selectedInquiry.product_name && (
              <div className="detail-row">
                <Text strong>Product:</Text>
                <Text>{selectedInquiry.product_name}</Text>
              </div>
            )}

            {selectedInquiry.service_name && (
              <div className="detail-row">
                <Text strong>Service:</Text>
                <Text>{selectedInquiry.service_name}</Text>
              </div>
            )}

            {selectedInquiry.partner_name && (
              <div className="detail-row">
                <Text strong>Partner:</Text>
                <Text>{selectedInquiry.partner_name}</Text>
              </div>
            )}

            <div className="detail-row">
              <Text strong>Submitted:</Text>
              <Text>
                {new Date(selectedInquiry.created_at).toLocaleString('el-GR')}
              </Text>
            </div>

            <div className="message-box">
              <Text strong>Your Message:</Text>
              <Paragraph>{selectedInquiry.message}</Paragraph>
            </div>

            {selectedInquiry.admin_response && (
              <div className="response-box">
                <Text strong>Response from Animap:</Text>
                <Paragraph>{selectedInquiry.admin_response}</Paragraph>
                {selectedInquiry.responded_at && (
                  <Text type="secondary">
                    Responded on: {new Date(selectedInquiry.responded_at).toLocaleString('el-GR')}
                  </Text>
                )}
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* New Inquiry Modal */}
      <InquiryModal
        visible={newInquiryModalVisible}
        onClose={handleNewInquiryClose}
        inquiryType="GENERAL"
      />
    </div>
  );
};

export default MyInquiriesPage;
